import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * SkeletonLoader Adapter Component
 * 
 * A customizable skeleton loader component for displaying loading states
 * that follows the design system's aesthetic principles.
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <SkeletonLoader className="h-4 w-32" />
 * 
 * // Text paragraph skeleton
 * <div className="space-y-2">
 *   <SkeletonLoader className="h-4 w-full" />
 *   <SkeletonLoader className="h-4 w-5/6" />
 *   <SkeletonLoader className="h-4 w-4/6" />
 * </div>
 * ```
 */
interface SkeletonLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant of the skeleton
   * @default "default"
   */
  variant?: "default" | "shimmer" | "pulse";
}

const SkeletonLoader = React.forwardRef<HTMLDivElement, SkeletonLoaderProps>(
  ({ className, variant = "shimmer", ...props }, ref) => {
    const baseClass = "bg-gray-200 dark:bg-gray-800 rounded-md premium-radius-md";
    
    // Apply animation variant
    const animationClass = 
      variant === "shimmer" 
        ? "animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 bg-[length:400%_100%]" 
        : variant === "pulse" 
          ? "animate-pulse" 
          : "";
          
    return (
      <div
        ref={ref}
        className={cn(baseClass, animationClass, className)}
        {...props}
      />
    );
  }
);

SkeletonLoader.displayName = "SkeletonLoader";

export { SkeletonLoader }; 